import React, { useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Send } from "lucide-react";
import { useLang } from "../i18n";
import { Reveal } from "./Reveal";
import { CONTACT, WHATSAPP_NUMBER } from "../lib/constants";

const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const Contact = () => {
  const { t } = useLang();
  const c = t.contact;
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    phone: "",
    email: "",
    company: "",
    company_size: "",
    message: "",
  });
  const [errors, setErrors] = useState({});

  const set = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const validate = () => {
    const er = {};
    if (!form.first_name.trim()) er.first_name = c.errors.firstName;
    if (form.phone.replace(/\D/g, "").length < 8) er.phone = c.errors.phone;
    if (!emailRe.test(form.email)) er.email = c.errors.email;
    if (!form.company.trim()) er.company = c.errors.company;
    if (!form.company_size) er.company_size = c.errors.companySize;
    if (!form.message.trim()) er.message = c.errors.message;
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const openWhatsApp = () => {
    const lines = [
      "*Nova solicitação de contato — Waltech*",
      `Nome: ${form.first_name} ${form.last_name}`.trim(),
      `Telefone: ${form.phone}`,
      `E-mail: ${form.email}`,
      `Empresa: ${form.company}`,
      `Tamanho: ${form.company_size}`,
      `Mensagem: ${form.message}`,
    ];
    const url = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(lines.join("\n"))}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const submit = (e) => {
    e.preventDefault();
    if (!validate()) return;
    toast.success(c.success);
    openWhatsApp();
    setForm({
      first_name: "",
      last_name: "",
      phone: "",
      email: "",
      company: "",
      company_size: "",
      message: "",
    });
  };

  const field =
    "w-full rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3.5 text-white placeholder:text-zinc-600 outline-none transition-colors duration-300 focus:border-[#00e5ff]/60";
  const errField = "border-red-500/60";

  const infoItems = [
    { icon: Phone, label: CONTACT.phone1, href: `tel:+55${WHATSAPP_NUMBER.slice(2)}` },
    { icon: Phone, label: CONTACT.phone2, href: "tel:+551123910229" },
    { icon: Mail, label: CONTACT.email, href: `mailto:${CONTACT.email}` },
  ];

  return (
    <section id="contato" data-testid="contact-section" className="relative py-24 md:py-32 border-t border-white/10">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {/* Info */}
          <div className="lg:col-span-5">
            <Reveal>
              <span className="font-mono-wt text-xs md:text-sm font-bold uppercase tracking-[0.25em] text-[#0047ff]">
                {c.overline}
              </span>
            </Reveal>
            <Reveal delay={0.05}>
              <h2 className="mt-4 font-display text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight text-white">
                {c.title}
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-5 text-base md:text-lg font-light text-zinc-400">{c.subtitle}</p>
            </Reveal>

            <Reveal delay={0.15} className="mt-10">
              <div className="font-mono-wt text-xs uppercase tracking-[0.2em] text-zinc-500">
                {c.infoTitle}
              </div>
              <div className="mt-4 space-y-3">
                {infoItems.map((it, i) => (
                  <a
                    key={i}
                    href={it.href}
                    data-testid={`contact-info-${i}`}
                    className="group flex items-center gap-3 text-zinc-300 hover:text-white transition-colors duration-300"
                  >
                    <span className="flex h-10 w-10 items-center justify-center rounded-full border border-white/10 group-hover:border-[#00e5ff]/50 transition-colors duration-300">
                      <it.icon className="h-4 w-4 text-[#00e5ff]" />
                    </span>
                    {it.label}
                  </a>
                ))}
              </div>
            </Reveal>

            <Reveal delay={0.2} className="mt-8">
              <div className="font-mono-wt text-xs uppercase tracking-[0.2em] text-zinc-500">
                {c.addressTitle}
              </div>
              <div className="mt-4 flex items-start gap-3 text-zinc-300">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-white/10">
                  <MapPin className="h-4 w-4 text-[#00e5ff]" />
                </span>
                <span className="leading-relaxed">
                  {CONTACT.addressLine1}
                  <br />
                  {CONTACT.addressLine2}
                  <br />
                  {CONTACT.addressLine3}
                </span>
              </div>
            </Reveal>
          </div>

          {/* Form */}
          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              <form
                onSubmit={submit}
                data-testid="contact-form"
                noValidate
                className="rounded-3xl border border-white/10 bg-[#0a0a0e] p-6 sm:p-10"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="mb-2 block text-sm text-zinc-400">{c.firstName} *</label>
                    <input
                      data-testid="input-first-name"
                      value={form.first_name}
                      onChange={set("first_name")}
                      className={`${field} ${errors.first_name ? errField : ""}`}
                      placeholder={c.firstName}
                    />
                    {errors.first_name && (
                      <p className="mt-1 text-xs text-red-400" data-testid="error-first-name">{errors.first_name}</p>
                    )}
                  </div>
                  <div>
                    <label className="mb-2 block text-sm text-zinc-400">{c.lastName}</label>
                    <input
                      data-testid="input-last-name"
                      value={form.last_name}
                      onChange={set("last_name")}
                      className={field}
                      placeholder={c.lastName}
                    />
                  </div>
                  <div>
                    <label className="mb-2 block text-sm text-zinc-400">{c.phone} *</label>
                    <input
                      data-testid="input-phone"
                      value={form.phone}
                      onChange={set("phone")}
                      className={`${field} ${errors.phone ? errField : ""}`}
                      placeholder="(11) 90000-0000"
                      inputMode="tel"
                    />
                    {errors.phone && (
                      <p className="mt-1 text-xs text-red-400" data-testid="error-phone">{errors.phone}</p>
                    )}
                  </div>
                  <div>
                    <label className="mb-2 block text-sm text-zinc-400">{c.email} *</label>
                    <input
                      data-testid="input-email"
                      value={form.email}
                      onChange={set("email")}
                      className={`${field} ${errors.email ? errField : ""}`}
                      placeholder="voce@empresa.com"
                      inputMode="email"
                    />
                    {errors.email && (
                      <p className="mt-1 text-xs text-red-400" data-testid="error-email">{errors.email}</p>
                    )}
                  </div>
                  <div>
                    <label className="mb-2 block text-sm text-zinc-400">{c.company} *</label>
                    <input
                      data-testid="input-company"
                      value={form.company}
                      onChange={set("company")}
                      className={`${field} ${errors.company ? errField : ""}`}
                      placeholder={c.company}
                    />
                    {errors.company && (
                      <p className="mt-1 text-xs text-red-400" data-testid="error-company">{errors.company}</p>
                    )}
                  </div>
                  <div>
                    <label className="mb-2 block text-sm text-zinc-400">{c.companySize} *</label>
                    <select
                      data-testid="input-company-size"
                      value={form.company_size}
                      onChange={set("company_size")}
                      className={`${field} ${errors.company_size ? errField : ""} ${form.company_size ? "text-white" : "text-zinc-600"}`}
                    >
                      <option value="" disabled className="bg-[#0a0a0e]">
                        {c.companySizePlaceholder}
                      </option>
                      {c.sizes.map((s) => (
                        <option key={s} value={s} className="bg-[#0a0a0e] text-white">
                          {s}
                        </option>
                      ))}
                    </select>
                    {errors.company_size && (
                      <p className="mt-1 text-xs text-red-400" data-testid="error-company-size">{errors.company_size}</p>
                    )}
                  </div>
                </div>

                <div className="mt-5">
                  <label className="mb-2 block text-sm text-zinc-400">{c.message} *</label>
                  <textarea
                    data-testid="input-message"
                    value={form.message}
                    onChange={set("message")}
                    maxLength={250}
                    rows={4}
                    className={`${field} resize-none ${errors.message ? errField : ""}`}
                    placeholder={c.message}
                  />
                  <div className="mt-1 flex justify-between">
                    {errors.message ? (
                      <p className="text-xs text-red-400" data-testid="error-message">{errors.message}</p>
                    ) : <span />}
                    <span className="text-xs text-zinc-600">{form.message.length}/250</span>
                  </div>
                </div>

                <button
                  type="submit"
                  data-testid="contact-submit"
                  className="group mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#0047ff] px-8 py-4 text-base font-semibold text-white hover:bg-[#2b5bff] transition-[background-color,box-shadow] duration-300 hover:shadow-[0_0_28px_rgba(0,71,255,0.5)]"
                >
                  {c.send}
                  <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                </button>
              </form>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
